/**
 * TypeStore Resolver Utility
 * 
 * Utilities for resolving TypeElement references from the typesStore
 * for use in dialog variables.
 */

import { typesStore, TypeItem, TypeElement } from '@/lib/stores/typesStore';

/**
 * Resolve a TypeElement by typeId and elementId
 * @param typeId - The TypeItem ID
 * @param elementId - The TypeElement ID within the TypeItem
 * @returns Object containing the element and typeItem, or null if not found
 */
export function resolveTypeElement(
  typeId: string,
  elementId: string
): { element: TypeElement; typeItem: TypeItem } | null {
  try {
    // Get all types from the store
    const allTypes = typesStore.getData();
    
    // Find the TypeItem
    const typeItem = allTypes.find(type => type.id === typeId);
    if (!typeItem) {
      console.warn(`TypeItem with id ${typeId} not found in typesStore`);
      return null;
    }
    
    // Find the TypeElement within the TypeItem's data
    const element = typeItem.data?.find(el => el.id === elementId);
    if (!element) {
      console.warn(`TypeElement with id ${elementId} not found in TypeItem ${typeId}`);
      return null;
    }
    
    return { element, typeItem };
  } catch (error) {
    console.error('Error resolving TypeElement:', error);
    return null;
  }
}

/**
 * Get the initial value for a TypeElement based on its configuration
 * @param element - The TypeElement to get the initial value for
 * @returns The initial value (boolean, number, or string)
 */
export function getInitialValue(element: TypeElement): boolean | number | string {
  // Check if element has a valueType configured
  if (element.valueType === 'single' && element.numericValue !== undefined) {
    return element.numericValue;
  }
  
  if (element.valueType === 'range') {
    // For range types, use the minimum value as initial value
    return element.rangeMin ?? 0;
  }
  
  // For elements without numeric values, return the element name as text
  return element.name;
}

/**
 * Get the variable type for a TypeElement based on its configuration
 * @param element - The TypeElement to get the type for
 * @param typeItem - The parent TypeItem for additional context
 * @returns The variable type ('boolean' | 'number' | 'text' | 'range')
 */
export function getVariableType(
  element: TypeElement,
  typeItem: TypeItem
): 'boolean' | 'number' | 'text' | 'range' {
  // Check TypeItem's element_type first
  if (typeItem.element_type === 'boolean') {
    return 'boolean';
  }
  
  // Check element's valueType
  if (element.valueType === 'range') {
    return 'range';
  }
  
  if (element.valueType === 'single' && element.numericValue !== undefined) {
    return 'number';
  }
  
  // Check TypeItem's element_type for number
  if (typeItem.element_type === 'number') {
    return 'number';
  }
  
  // Default to text for text-based types or elements without numeric values
  return 'text';
}

/**
 * Get all TypeItems for a specific game from the store
 * @param gameId - The game ID to filter by
 * @returns Array of TypeItems for the specified game
 */
export function getTypeItemsForGame(gameId: string): TypeItem[] {
  const allTypes = typesStore.getData();
  return allTypes.filter(type => type.game_id === gameId);
}

/**
 * Get a TypeItem by ID
 * @param typeId - The TypeItem ID
 * @returns The TypeItem or null if not found
 */
export function getTypeItemById(typeId: string): TypeItem | null {
  const allTypes = typesStore.getData();
  return allTypes.find(type => type.id === typeId) || null;
}

/**
 * Validate that a value is within the allowed range for a range-type element
 * @param value - The value to validate
 * @param element - The TypeElement with range configuration
 * @returns The clamped value within the range
 */
export function clampToRange(value: number, element: TypeElement): number {
  const min = element.rangeMin ?? 0;
  const max = element.rangeMax ?? 100;
  return Math.max(min, Math.min(max, value));
}

/**
 * Get the increment value for a range-type element
 * @param element - The TypeElement with range configuration
 * @returns The increment value (defaults to 1 if not specified)
 */
export function getRangeIncrement(element: TypeElement): number {
  return element.increment ?? 1;
}

/**
 * Check if a value falls within a range element's bounds
 * @param value - The numeric value to check
 * @param element - The TypeElement with range configuration
 * @returns True if the value is within the element's range
 */
export function isValueInRange(value: number, element: TypeElement): boolean {
  if (element.valueType !== 'range') return false;
  
  const min = element.rangeMin ?? 0;
  const max = element.rangeMax ?? 100;
  
  return value >= min && value <= max;
}

/**
 * Find which range element a value falls into from a TypeItem
 * @param value - The numeric value to check
 * @param typeId - The TypeItem ID containing range elements
 * @returns The TypeElement that the value falls into, or null
 */
export function findRangeForValue(value: number, typeId: string): TypeElement | null {
  const typeItem = getTypeItemById(typeId);
  if (!typeItem || !typeItem.data) return null;
  
  // Find the first range element that contains the value
  for (const element of typeItem.data) {
    if (element.valueType === 'range' && isValueInRange(value, element)) {
      return element;
    }
  }
  
  return null;
}
